<template>
  <header
    ref="header"
    class="header"
    :class="headerClass"
    tabindex="0">
    <div class="header__container">
      <div class="header-brand-switch">
        <LocLink to="/" class="header-navbar__logo">
          <img :src="`/image/logo/logo-${brand}.svg`" :alt="brand" />
        </LocLink>
        <CountrySwitcher v-if="!isShowSwitcher" />
      </div>    

      <nav class="header-navbar">
        <ul class="navbar-list">
          <li>
            <LocLink class="navbar-list__link text-16-500" to="/facilities">
              {{ $t("Facilities") }}
            </LocLink>
          </li>
          <li>
            <LocLink class="navbar-list__link text-16-500" to="/levels">
              {{ $t("Memberships") }}
            </LocLink>
          </li>
          <li>
            <LocLink class="navbar-list__link text-16-500" to="/companies">
              {{ $t("For Companies") }}
            </LocLink>
          </li>
          <li>
            <LocLink class="navbar-list__link text-16-500" to="/partners">
              {{ $t("For Partners") }}
            </LocLink>
          </li>
          <li>
            <LocLink class="navbar-list__link text-16-500" to="/contacts">
              {{ $t("Contacts") }}
            </LocLink>
          </li>
        </ul>
      </nav>
      <div class="header-controls">
        <BaseButton
          id="offer-btn"
          class="header-controls__offer"
          small
          :outlined="isOutlinedButton"
          @click="showGetAnOfferFormModal"
        >
          {{ $t("Get an Offer") }}
        </BaseButton>
        <BaseButton
          class="header-controls__offer"
          text
          @click="showAskQuestionModal"
        >
          {{ $t("Ask Us a Question") }}
        </BaseButton>
        <div>
          <div
            id="toggle-sidebar"
            tabindex="0"
            class="header-controls__menu"
            :class="showSidebarClass"
            @click.stop="toggleSidebar"
          >
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        <ClientOnly>
          <Teleport to="body">
            <LazySidebarTheSidebar
              :show-sidebar="showSidebar"
              :class="headerClass"
              :brand="brand"
              @close="closeSidebar"
            />
          </Teleport>
        </ClientOnly>
      </div>
    </div>
    <LazyModalsAskQuestionFormModal
      v-if="mountAskQuestionModal"
      v-model:is-show-modal="isShowAskQuestionModal"
    />
  </header>
</template>

<script setup lang="ts">
import LocLink from "~/components/lang/LocLink.vue";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import BaseButton from "~/components/button/BaseButton.vue";
import CountrySwitcher  from "~/components/input/CountrySwitcher.vue";
import { useGetAnOfferFormModal } from "~/composables/modals/getAnOfferFormModal/useGetAnOfferFormModal";
import { useConfigStore } from "~/stores/config";
import { storeToRefs } from "pinia";

const { gtag } = useGtag();
const route = useRoute();
const { brand } = useRuntimeConfig().public;
const { locale } = useI18n();
const { showModal } = useGetAnOfferFormModal();

const { supportsArchivedSubscriptions: isShowSwitcher } = storeToRefs(useConfigStore());

const mountAskQuestionModal = ref(false);
const isShowAskQuestionModal = ref<boolean>(false);

function showAskQuestionModal() {
  !mountAskQuestionModal.value && (mountAskQuestionModal.value = true);
  isShowAskQuestionModal.value = true;
  gtag("event", HeaderEvents.askQuestionModal);
}

const isScrollMode = ref(false);
const isFacilitiesPage = computed(() => {
  const regexp = new RegExp(`^/${locale.value}/facilities$`, "g");
  return route.path.match(regexp);
});
const isOutlinedButton = computed(() => {
  return !isFacilitiesPage.value && !isScrollMode.value;
});
const header = ref<HTMLElement | null>(null);
const headerClass = computed(() => ({
  "scroll-mode":
    (isFacilitiesPage.value || isScrollMode.value) && !showSidebar.value,
}));

function showGetAnOfferFormModal() {
  gtag("event", HeaderEvents.getOfferModal);
  showModal(true);
}

const showSidebar = ref(false);
const showSidebarClass = computed(() =>
  showSidebar.value ? "opened" : ""
);
function toggleSidebar() {
  showSidebar.value = !showSidebar.value;
}
function closeSidebar() {
  showSidebar.value = false;
}

let observer: IntersectionObserver | null = null;

function createObserver(): void {
  observer = new IntersectionObserver(
    ([event]) => {
      if (event.intersectionRatio < 1) {
        isScrollMode.value = true;
      } else {
        isScrollMode.value = false;
      }
    },
    { threshold: [1] }
  );

  if (header.value) {
    observer.observe(header.value);
  }
}

onMounted(createObserver);

onUnmounted(() => {
  if (header.value) {
    observer?.unobserve(header.value);
  }
});
</script>

<style>
.header-brand-switch {
  margin-right: 10px;
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 10px
}

.header-brand-switch .select-header .select-field__control {
  border: none;
  background: none;
}

.header-brand-switch .select-header .select-options{
  width: 200px;
  left: -30px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
</style>